<template>
  <ContextColorProvider context="trade" class="faq">
    <header>
      <NavBar sticky>
        <template #left>
          <BackButton />
        </template>
      </NavBar>
    </header>

    <div v-if="hasError" class="faq__error">
      <HelperViewStatus
        class="stock-lending__error"
        generic-error
        button-label="Tentar novamente"
        @click="fetchFAQ"
      />
    </div>

    <div v-else-if="loading" class="faq__loading">
      <LoaderIndicator with-container />
    </div>
    <template v-else>
      <div class="faq__container">
        <HeadingDisplay :title="faq?.title" />

        <AccordionWrapper class="faq__container__accordion-wrapper">
          <template v-for="item in faq?.questions">
            <Accordion :key="item.question" :title="item.question">
              <BaseText
                size="p5"
                as="p"
                :color="NebraskaColors.textSecondary"
                v-html="item.answer"
              />
            </Accordion>
          </template>
        </AccordionWrapper>

        <BaseText
          size="p5"
          class="faq__container__footer"
          as="p"
          :color="NebraskaColors.textSecondary"
          v-html="footerContent(faq?.faqUrl)"
        />
      </div>
    </template>
  </ContextColorProvider>
</template>

<script lang="ts" setup>
import {
  BaseText,
  ContextColorProvider,
  HeadingDisplay,
  HelperViewStatus,
  LoaderIndicator,
  NavBar
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import {
  Accordion,
  AccordionWrapper
} from '@/modules/common/components/Accordion';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { onMounted, ref } from 'vue';
import { FAQ, getFAQ } from '@/modules/trade/services/paid-custody';

const faq = ref<FAQ | null>(null);
const hasError = ref(false);
const loading = ref(false);

onMounted(async () => {
  fetchFAQ();
});

const fetchFAQ = async () => {
  try {
    loading.value = true;
    hasError.value = false;

    const response = await getFAQ();

    faq.value = response;

    return true;
  } catch (e) {
    hasError.value = true;

    return false;
  } finally {
    loading.value = false;
  }
};

const footerContent = (faqUrl: string | undefined) => {
  return `
    Ainda com dúvidas? Acesse o nosso <a href="${faqUrl}">FAQ</a> ou entre em contato pelo
    e-mail <a href="mailto:meajuda@warren.com.br">meajuda@warren.com.br</a>.
    `;
};
</script>

<style scoped lang="less">
.faq {
  &__container {
    max-width: 629px;
    margin: 0 auto;
    padding: @size-spacing-x1000 @size-spacing-x400;

    &__accordion-wrapper {
      padding: @size-spacing-x800 0;

      :deep(.wrn-accordion:first-child) {
        border-top: none;
      }

      :deep(.wrn-accordion-button > p) {
        padding-top: @size-spacing-x450 !important;
        padding-bottom: @size-spacing-x450 !important;
      }

      :deep(.wrn-accordion-text) {
        margin-bottom: 0 !important;
        padding-bottom: @size-spacing-x450 !important;
      }

      :deep(.wrn-accordion__actions .svg-icon > path) {
        fill: @element-secondary;
      }
    }

    &__footer {
      max-width: 430px;

      :deep(a) {
        color: @brand-link !important;
        text-decoration: underline;
      }
    }
  }

  &__loading,
  &__error {
    height: 80vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
